import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import {
  isInstalledPWA,
  isPushSupported,
  recoverPushSubscriptionIfNeeded,
  resetPushNotifications,
  subscribePushNotifications,
  unsubscribePushNotifications,
} from '../lib/pushSubscription.js';
import { IosInstallHint } from './IosInstallHint.js';

type State =
  | { kind: 'loading' }
  | { kind: 'unsupported' }
  | { kind: 'ios_not_installed' }
  | { kind: 'enabled' }
  | { kind: 'disabled' }
  | { kind: 'busy' }
  | { kind: 'error'; reason: string };

type TestState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'ok'; sent: number; total: number }
  | { kind: 'fail'; reason: string };

type ResetState =
  | { kind: 'idle' }
  | { kind: 'resetting' }
  | { kind: 'done'; deletedServerRows: number }
  | { kind: 'fail'; reason: string };

/**
 * Phase 1 affordance for opting in to push reminders. Deliberately
 * understated — it sits at the bottom of the Obligations page, not
 * in the primary capture / triage flow. Phase 3 will surface a
 * contextual prompt the first time a user sets a `dueTime` on a
 * task; this block stays as the always-available switch for
 * managing the state per-device.
 *
 * State machine drives the rendered copy + action:
 *  - `loading`           — initial mount, checking the current
 *                          subscription
 *  - `unsupported`       — no PushManager / Notification API
 *                          (older browsers, in-app WebViews)
 *  - `ios_not_installed` — iOS Safari before "Add to home screen"
 *                          was used; push only works for installed
 *                          PWAs on iOS
 *  - `disabled`          — supported and idle, no subscription
 *                          present
 *  - `enabled`           — subscription registered with the
 *                          backend; the cron worker (Phase 2) will
 *                          deliver pushes to this device
 *  - `busy`              — mid-flight subscribe/unsubscribe
 *  - `error`             — last attempt failed; user sees the
 *                          reason and a retry option
 */
// Threshold for the silently-broken warning. The cron fires at
// most once per timed obligation per occurrence, so three unacked
// pushes typically span ~3 distinct reminders — enough to filter
// transient delivery hiccups (device asleep, APNs blip) without
// letting an actually-broken device run for weeks before the
// user notices.
const SILENT_PUSH_THRESHOLD = 3;

export function ReminderToggle() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [testState, setTestState] = useState<TestState>({ kind: 'idle' });
  const [resetState, setResetState] = useState<ResetState>({ kind: 'idle' });
  // Number of pushes the server has fired to this device's
  // subscription that the SW hasn't acked back. Null while we
  // haven't fetched (or the toggle isn't 'enabled' yet). Used to
  // detect the iOS Chrome-installed-PWA failure mode where APNs
  // accepts every push and the SW push event never fires.
  const [unackedCount, setUnackedCount] = useState<number | null>(null);
  // Bumped after operations that should refresh the health view
  // (e.g. a "Send test" completing — the SW may have just acked).
  const [healthTrigger, setHealthTrigger] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      // iOS exposes the PushManager / Notification APIs *only*
      // inside an installed PWA — in a regular Safari tab they
      // don't exist at all. So check the iOS-not-installed
      // condition first; otherwise users in a fresh Safari tab
      // land in `unsupported` and never see the install
      // walkthrough that would unblock them. `isInstalledPWA()`
      // returns true unconditionally on non-iOS, so a false
      // result here uniquely identifies the iOS-not-installed
      // case.
      if (!isInstalledPWA()) {
        if (!cancelled) setState({ kind: 'ios_not_installed' });
        return;
      }
      if (!isPushSupported()) {
        if (!cancelled) setState({ kind: 'unsupported' });
        return;
      }
      // Silent recovery handles the common "iOS replaced the SW
      // and invalidated the subscription" case. If the user
      // previously opted in and the OS permission is still
      // granted, it transparently re-issues a fresh subscription
      // and POSTs it (deviceId-keyed upsert keeps the server row
      // count at one). On a clean install with no prior intent,
      // it just returns null and we render the "disabled" state.
      try {
        const sub = await recoverPushSubscriptionIfNeeded();
        if (cancelled) return;
        setState({ kind: sub ? 'enabled' : 'disabled' });
      } catch {
        if (cancelled) return;
        // Silent recovery failed (network, permission flip, etc).
        // Drop back to the disabled state so the user can drive
        // the explicit re-enable flow with a real error surface.
        setState({ kind: 'disabled' });
      }
    }
    void check();
    return () => {
      cancelled = true;
    };
  }, []);

  // Health check — runs whenever we've reached the 'enabled'
  // state, fetches the server's per-subscription counters, and
  // pulls out the one matching this device's current
  // PushSubscription endpoint. The number drives the silently-
  // broken warning further down the render.
  //
  // Also re-runs after a successful "Send test" so the user gets
  // immediate feedback if the test push acked (counter drops to
  // 0) vs. didn't (counter stays climbing).
  useEffect(() => {
    if (state.kind !== 'enabled') {
      setUnackedCount(null);
      return;
    }
    let cancelled = false;
    async function checkHealth() {
      try {
        const reg = await navigator.serviceWorker.ready;
        const current = await reg.pushManager.getSubscription();
        if (cancelled || !current) return;
        const subs = await api.listSubscriptions();
        if (cancelled) return;
        const mine = subs.find((s) => s.endpoint === current.endpoint);
        setUnackedCount(mine ? mine.pushesSinceAck : null);
      } catch {
        // Health is opportunistic — a network blip just means
        // we don't show the warning this tick. The next mount
        // or the next "Send test" will try again.
      }
    }
    void checkHealth();
    return () => {
      cancelled = true;
    };
  }, [state.kind, healthTrigger]);

  async function enable() {
    setState({ kind: 'busy' });
    try {
      await subscribePushNotifications();
      setState({ kind: 'enabled' });
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown';
      setState({ kind: 'error', reason });
    }
  }

  async function disable() {
    setState({ kind: 'busy' });
    try {
      await unsubscribePushNotifications();
      setState({ kind: 'disabled' });
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown';
      setState({ kind: 'error', reason });
    }
  }

  async function reset() {
    setResetState({ kind: 'resetting' });
    setTestState({ kind: 'idle' });
    try {
      const { deletedServerRows } = await resetPushNotifications();
      setResetState({ kind: 'done', deletedServerRows });
      setState({ kind: 'enabled' });
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown';
      setResetState({ kind: 'fail', reason });
    }
  }

  async function sendTest() {
    setTestState({ kind: 'sending' });
    try {
      const result = await api.testPush();
      const total = result.results.length;
      const sent = result.results.filter((r) => r.ok).length;
      if (sent > 0) {
        setTestState({ kind: 'ok', sent, total });
      } else if (total === 0) {
        setTestState({ kind: 'fail', reason: 'No subscriptions on the server.' });
      } else {
        // Show the first non-ok result's reason — usually one device
        // is enough to tell us what's failing.
        const first = result.results.find((r) => !r.ok);
        setTestState({
          kind: 'fail',
          reason: first ? `${first.status}: ${first.reason}` : 'unknown',
        });
      }
    } catch (err) {
      setTestState({
        kind: 'fail',
        reason: err instanceof Error ? err.message : 'unknown',
      });
    } finally {
      // Give the SW a moment to handle the push and POST its
      // ack before we re-poll. If the test acks, the warning
      // will disappear; if it doesn't, the counter will be a
      // step higher and the user has confirmation of the
      // failure mode.
      window.setTimeout(() => setHealthTrigger((c) => c + 1), 1500);
    }
  }

  // The not-installed walkthrough needs more vertical room than
  // the standard horizontal "label on left, buttons on right"
  // layout allows. Render a dedicated column variant for it.
  if (state.kind === 'ios_not_installed') {
    return (
      <div className="reminder-toggle reminder-toggle--column">
        <span className="reminder-toggle__heading">Reminders</span>
        <IosInstallHint />
      </div>
    );
  }

  const showSilentWarning =
    state.kind === 'enabled' &&
    unackedCount !== null &&
    unackedCount >= SILENT_PUSH_THRESHOLD;

  return (
    <>
      <div className="reminder-toggle">
        <div className="reminder-toggle__label">
          <span className="reminder-toggle__heading">Reminders</span>
          <span className="reminder-toggle__status">{statusText(state)}</span>
          {state.kind === 'enabled' && testState.kind !== 'idle' && (
            <span className="reminder-toggle__status">{testStatusText(testState)}</span>
          )}
          {state.kind === 'enabled' && resetState.kind !== 'idle' && (
            <span className="reminder-toggle__status">{resetStatusText(resetState)}</span>
          )}
        </div>
        <div className="reminder-toggle__actions">
          {state.kind === 'enabled' && (
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={reset}
              disabled={resetState.kind === 'resetting'}
            >
              {resetState.kind === 'resetting' ? 'Resetting…' : 'Reset'}
            </button>
          )}
          {state.kind === 'enabled' && (
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={sendTest}
              disabled={testState.kind === 'sending'}
            >
              {testState.kind === 'sending' ? 'Sending…' : 'Send test'}
            </button>
          )}
          {renderAction(state, enable, disable)}
        </div>
      </div>
      {showSilentWarning && (
        <div className="reminder-warning" role="status">
          <p className="reminder-warning__title">
            Reminders aren&rsquo;t reaching this device.
          </p>
          <p className="reminder-warning__detail">
            The server has fired {unackedCount} pushes that this device
            hasn&rsquo;t acknowledged. Most commonly this means the PWA
            was installed via Chrome, which iOS won&rsquo;t deliver
            push notifications to — reinstall via Safari to fix.
          </p>
          <IosInstallHint />
        </div>
      )}
    </>
  );
}

function resetStatusText(state: ResetState): string {
  switch (state.kind) {
    case 'idle':
      return '';
    case 'resetting':
      return 'Clearing old subscriptions and re-registering…';
    case 'done':
      return state.deletedServerRows === 0
        ? 'Re-registered. No old subscriptions to clear.'
        : `Cleared ${state.deletedServerRows} old subscription${state.deletedServerRows === 1 ? '' : 's'} and re-registered.`;
    case 'fail':
      return `Reset failed — ${state.reason}`;
  }
}

function testStatusText(state: TestState): string {
  switch (state.kind) {
    case 'idle':
      return '';
    case 'sending':
      return 'Sending test push…';
    case 'ok':
      return state.total === 1
        ? 'Test push sent — check your device.'
        : `Test push sent to ${state.sent} of ${state.total} devices.`;
    case 'fail':
      return `Test failed — ${state.reason}`;
  }
}

function statusText(state: State): string {
  switch (state.kind) {
    case 'loading':
      return 'Checking…';
    case 'unsupported':
      return 'Not supported on this browser.';
    case 'ios_not_installed':
      return 'Add Almanac to your home screen first, then come back here.';
    case 'enabled':
      return 'On for this device.';
    case 'disabled':
      return 'Off. Turn on to receive reminders when tasks come due.';
    case 'busy':
      return 'Just a moment…';
    case 'error':
      return errorText(state.reason);
  }
}

function errorText(reason: string): string {
  switch (reason) {
    case 'permission_denied':
      return 'Notification permission was declined. Reset it in your browser settings to try again.';
    case 'not_configured':
      return 'Reminder service is not configured yet. Try again later.';
    case 'ios_not_installed':
      return 'Add Almanac to your home screen first, then come back here.';
    case 'unsupported':
      return 'Not supported on this browser.';
    default:
      return `Something went wrong (${reason}).`;
  }
}

function renderAction(
  state: State,
  enable: () => void,
  disable: () => void,
): React.ReactNode {
  if (state.kind === 'enabled') {
    return (
      <button type="button" className="btn btn--ghost btn--small" onClick={disable}>
        Turn off
      </button>
    );
  }
  if (state.kind === 'disabled' || state.kind === 'error') {
    return (
      <button type="button" className="btn btn--secondary btn--small" onClick={enable}>
        Turn on
      </button>
    );
  }
  // loading / busy / unsupported — no action. (ios_not_installed
  // is short-circuited above into a dedicated walkthrough layout
  // and never reaches this function.)
  return null;
}
